'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'
import { cn } from '@/utilities/ui'
import ErrorBoundary from '@/components/ui/ErrorBoundary'
import { CMSLink } from '@/components/Link'
import type { Page } from '@/payload-types'
import { Media } from '@/components/Media'
import { getPosterUrl } from '@/utilities/getPosterUrl'

interface HeroClientProps {
  slides: NonNullable<Page['hero']['slides']>
  settings?: Page['hero']['settings']
}

function HeroContent({ slides, settings = {} }: HeroClientProps) {
  const SLIDE_DURATION = settings.slideDuration || 10000
  const [currentSlide, setCurrentSlide] = useState(0)
  const [previousSlide, setPreviousSlide] = useState<number | null>(null)
  const [progress, setProgress] = useState(0)
  const [slideProgress, setSlideProgress] = useState(new Array(slides.length).fill(0))
  // Which slides are allowed to load their <Media>. Only the first slide loads
  // up front so it (the LCP image) isn't starved by the other slides; the rest
  // are warmed just-in-time. A slide stays in the set once added, so returning
  // to it never re-decodes (matching the "keep mounted" behaviour below).
  const [loadedSlides, setLoadedSlides] = useState<Set<number>>(() => new Set([0]))
  const autoAdvanceRef = useRef<NodeJS.Timeout | null>(null)

  const markSlideLoaded = useCallback(
    (...indices: number[]) => {
      setLoadedSlides((prev) => {
        let next: Set<number> | null = null
        for (const raw of indices) {
          const i = ((raw % slides.length) + slides.length) % slides.length
          if (!prev.has(i) && !next?.has(i)) {
            next = next ?? new Set(prev)
            next.add(i)
          }
        }
        return next ?? prev
      })
    },
    [slides.length],
  )
  const parallaxRef = useRef<HTMLDivElement | null>(null)

  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ['start end', 'end start'],
  })

  const y = useTransform(scrollYProgress, [0, 1], [-60, 60])

  useEffect(() => {
    if (!settings.autoAdvance) return

    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current)
    }

    autoAdvanceRef.current = setInterval(() => {
      setCurrentSlide((prev) => {
        setPreviousSlide(prev)
        return (prev + 1) % slides.length
      })
      setProgress(0)
    }, SLIDE_DURATION)

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current)
      }
    }
  }, [currentSlide, slides.length, SLIDE_DURATION, settings.autoAdvance])

  useEffect(() => {
    if (!settings.autoAdvance) return

    setProgress(0)
    const startTime = Date.now()

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.min((elapsed / SLIDE_DURATION) * 100, 100)
      setProgress(newProgress)

      if (newProgress >= 100) {
        clearInterval(progressInterval)
      }
    }, 50)

    return () => clearInterval(progressInterval)
  }, [currentSlide, SLIDE_DURATION, settings.autoAdvance])

  useEffect(() => {
    setSlideProgress((prev) => {
      const newProgress = [...prev]

      if (currentSlide === 0 && progress === 0) {
        return new Array(slides.length).fill(0)
      }

      for (let i = 0; i < currentSlide; i++) {
        newProgress[i] = 100
      }
      newProgress[currentSlide] = progress
      return newProgress
    })
  }, [currentSlide, progress, slides.length])

  // Warm the second slide once the initial load has settled — after the window
  // `load` event (so the LCP image has already had the bandwidth) and then an
  // idle tick. With a 10s slide duration there is ample time before slide 2 is
  // shown, so it never competes with the hero image on first paint.
  useEffect(() => {
    if (slides.length <= 1) return
    let cancelled = false
    let idleId: number | undefined
    const warmSecond = () => {
      if (!cancelled) markSlideLoaded(1)
    }
    const schedule = () => {
      const ric = (window as unknown as { requestIdleCallback?: typeof requestIdleCallback })
        .requestIdleCallback
      idleId =
        typeof ric === 'function'
          ? ric(warmSecond, { timeout: 3000 })
          : window.setTimeout(warmSecond, 1500)
    }
    if (document.readyState === 'complete') {
      schedule()
    } else {
      window.addEventListener('load', schedule, { once: true })
    }
    return () => {
      cancelled = true
      window.removeEventListener('load', schedule)
      if (idleId !== undefined) {
        const cancelIdle = (window as unknown as { cancelIdleCallback?: (id: number) => void })
          .cancelIdleCallback
        if (cancelIdle) cancelIdle(idleId)
        else window.clearTimeout(idleId)
      }
    }
  }, [slides.length, markSlideLoaded])

  // Once the carousel actually moves, keep the previous/current/next slides warm
  // so upcoming transitions are seamless. `previousSlide` is null until the first
  // advance, which is what keeps slide 2+ off the initial-load critical path.
  useEffect(() => {
    if (previousSlide === null) return
    markSlideLoaded(previousSlide, currentSlide, currentSlide + 1)
  }, [currentSlide, previousSlide, markSlideLoaded])

  const goToSlide = (index: number) => {
    setCurrentSlide((prev) => {
      if (prev === index) return prev
      setPreviousSlide(prev)
      return index
    })
    setProgress(0)

    setSlideProgress((prev) => {
      const newProgress = [...prev]
      for (let i = index + 1; i < slides.length; i++) {
        newProgress[i] = 0
      }
      return newProgress
    })
  }

  const getHeightClass = () => {
    // Default to full screen height since height setting is not available in heroFields
    return 'h-dvh'
  }

  const renderSlideMedia = (
    slide: (typeof slides)[number],
    priority: boolean,
    disableBlur: boolean,
  ) => {
    const mediaInner = (
      <>
        {slide.backgroundType === 'image' &&
          typeof slide.backgroundImage === 'object' &&
          slide.backgroundImage && (
            <Media
              resource={slide.backgroundImage}
              fill
              pictureClassName="absolute inset-0 w-full h-full"
              imgClassName="object-cover"
              priority={priority}
              fetchPriority={priority ? 'high' : 'auto'}
              size="100vw"
              disableBlur={disableBlur}
            />
          )}

        {slide.backgroundType === 'video' && slide.backgroundVideo && (
          <div className="absolute inset-0">
            {typeof slide.backgroundVideo.videoFile === 'object' &&
            slide.backgroundVideo.videoFile ? (
              <video
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                poster={getPosterUrl(slide.backgroundVideo.posterImage)}
              >
                <source src={slide.backgroundVideo.videoFile.url || ''} type="video/mp4" />
              </video>
            ) : slide.backgroundVideo.videoUrl ? (
              <iframe
                className="w-full h-full object-cover"
                src={slide.backgroundVideo.videoUrl}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            ) : null}
          </div>
        )}
      </>
    )

    // Only promote to a compositor layer when parallax is active. Otherwise
    // a fresh `will-change-transform` element on every slide change causes
    // mobile Safari to paint a black frame while the new GPU layer is created.
    if (settings.enableParallax) {
      return (
        <motion.div className="absolute inset-0 will-change-transform" style={{ y }}>
          {mediaInner}
        </motion.div>
      )
    }

    return <div className="absolute inset-0">{mediaInner}</div>
  }

  return (
    <section ref={parallaxRef} className={cn('relative overflow-hidden', getHeightClass())}>
      {/* All slides stay mounted so their <Media> components keep their loaded */}
      {/* state across slide changes — no remount means no re-decode flash. */}
      {/* Current slide: z-[2], opacity-100. Previous slide: z-[1], opacity-100 */}
      {/* (underlay prevents background bleed-through). All others: z-0, opacity-0. */}
      {slides.map((slide, index) => {
        const isCurrent = index === currentSlide
        const isPrevious = index === previousSlide && previousSlide !== currentSlide

        return (
          <div
            key={`slide-${index}`}
            className={cn(
              'absolute inset-0 transition-opacity duration-1000 ease-in-out',
              isCurrent
                ? 'z-[2] opacity-100'
                : isPrevious
                  ? 'z-[1] opacity-100'
                  : 'z-0 opacity-0',
            )}
            aria-hidden={!isCurrent}
          >
            {/* Only mount a slide's media once it's been warmed. Mounting is the
                real deferral mechanism here: every slide is full-bleed in the
                viewport, so the browser ignores `loading="lazy"` and would
                otherwise fetch all of them on first paint. */}
            {loadedSlides.has(index) ? renderSlideMedia(slide, index === 0, false) : null}
          </div>
        )
      })}

      {/* Gradient overlay - dark fade-in from bottom for better text visibility */}
      {/* Placed outside slides loop so it's always visible regardless of active slide */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent z-10" />

      {/* Content Container */}
      <div className="absolute bottom-4 md:bottom-14 left-0 right-0 z-20">
        <div className="container mx-auto px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              className="text-left text-white max-w-6xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.8,
                ease: [0.25, 0.46, 0.45, 0.94],
                staggerChildren: 0.1,
              }}
            >
              {slides[currentSlide].slideTitle && (
                <motion.div
                  className="display-lg  on-dark uppercase"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  {slides[currentSlide].slideTitle}
                </motion.div>
              )}
              {slides[currentSlide].slideSubtitle && (
                <motion.p
                  className="subtitle-2xl mb-8 on-dark-secondary"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.2,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  {slides[currentSlide].slideSubtitle}
                </motion.p>
              )}
              {Array.isArray((slides[currentSlide] as any).links) &&
                (slides[currentSlide] as any).links.length > 0 && (
                  <motion.div
                    className="flex flex-wrap gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.4,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                  >
                    {(
                      (slides[currentSlide] as any).links as Array<{
                        link?: any
                      }>
                    ).map((linkItem, i) => {
                      if (!linkItem.link) return null

                      const link = linkItem.link

                      return (
                        <CMSLink
                          key={i}
                          {...link}
                          newTab={link.openInNewTab}
                          appearance="external"
                          size="lg"
                          externalColor="light"
                        />
                      )
                    })}
                  </motion.div>
                )}
            </motion.div>
          </AnimatePresence>

          {/* Progress Bars */}
          <div className="flex space-x-5 w-full mt-8">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="group cursor-pointer flex-1 py-3"
                aria-label={`Gehe zu Slide ${index + 1}`}
              >
                <div className="w-full h-1 bg-white/30 overflow-hidden transition-all duration-200 group-hover:bg-white/50">
                  <div
                    className="h-full bg-blue-300 transition-all duration-50 ease-linear"
                    style={{ width: `${slideProgress[index]}%` }}
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export const HeroClient: React.FC<HeroClientProps> = (props) => {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Hero component error:', error, errorInfo)
      }}
    >
      <HeroContent {...props} />
    </ErrorBoundary>
  )
}
